import React, { useMemo } from "react";
import JSZip from "jszip";
import axios from "axios";
import { saveAs } from "file-saver";
import {
  FaFileAlt,
  FaEye,
  FaPrint,
  FaChevronUp,
  FaChevronDown,
  FaPalette,
  FaCopy,
  FaBook,
  FaFile,
  FaPaperclip,
  FaPaperPlane,
  FaCompass,
  FaLayerGroup,
  FaStickyNote,
  FaDownload,
} from "react-icons/fa";

const ViewFilesModal = ({
  isOpen,
  onClose,
  currentOrderFiles,
  currentOrderId,
  expandedGroupIndex,
  toggleGroupDetails,
  handleViewFile,
  handlePrint,
}) => {
  // Function to generate a unique key for grouping based on specified attributes
  const getCustomizationKey = (file) => {
    const {
      colorType = "Unknown",
      paperSize = "Unknown",
      pagesPerSheet = "Unknown",
      orientation = "Unknown",
      paperType = "Unknown",
      wantBinding = false,
      note = "", // Treat undefined or null notes as empty string
      copies = 1,
      totalPages = "Unknown",
      pageRange = "all",
    } = file;
    return `${colorType}|${paperSize}|${pagesPerSheet}|${orientation}|${paperType}|${wantBinding}|${note}|${copies}|${totalPages}|${pageRange}`;
  };

  // Group files by specified attributes using useMemo for performance
  const groupedFiles = useMemo(() => {
    const groups = {};
    currentOrderFiles.forEach((file, index) => {
      const key = getCustomizationKey(file);
      if (!groups[key]) {
        groups[key] = {
          files: [],
          customizations: {
            colorType: file.colorType,
            paperSize: file.paperSize,
            pagesPerSheet: file.pagesPerSheet,
            orientation: file.orientation,
            paperType: file.paperType,
            wantBinding: file.wantBinding,
            note: file.note,
            copies: file.copies,
            totalPages: file.totalPages,
            pageRange: file.pageRange,
          },
        };
      }
      groups[key].files.push({ ...file, originalIndex: index });
    });
    return Object.values(groups);
  }, [currentOrderFiles]);

  // Function to download all files in a group as a ZIP
 const handleDownloadGroup = async (group) => {
   try {
     const files = group.files.map((file) => ({
       fileURL: file.fileURL,
       originalName: file.originalName,
     }));

     console.log("Sending files to proxy:", files);

     // Call the Cloud Function
     const response = await axios.post(
       "https://downloadfilesaszip-qwfnnmalla-uc.a.run.app",
       { files },
       { responseType: "blob" } // Important for binary data
     );

     // Trigger download
     const blob = new Blob([response.data], { type: "application/zip" });
     saveAs(blob, `Group_${group.files[0].originalIndex + 1}_Files.zip`);
   } catch (error) {
     console.error("Error downloading ZIP:", error);
     // Optionally show a toast notification
     alert("Failed to download files. Please try again.");
   }
 };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-60 z-10 transition-opacity duration-300">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 sm:mx-6 lg:mx-8 p-6 max-h-[85vh] overflow-y-auto relative">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white z-10 pt-2 pb-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">
              Files for Order #{currentOrderId}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 transition-colors duration-200"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {groupedFiles.length === 0 ? (
            <p className="text-center text-gray-500 text-lg">No files available for this order.</p>
          ) : (
            groupedFiles.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="bg-gray-50 rounded-lg shadow-sm transition-shadow duration-200 hover:shadow-md"
              >
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-gray-100 rounded-t-lg">
                  <div className="flex items-center space-x-3">
                    <FaFileAlt className="text-blue-600 text-lg" />
                    <span className="text-gray-800 font-semibold text-lg">
                      Group {groupIndex + 1} ({group.files.length}{" "}
                      {group.files.length === 1 ? "file" : "files"})
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    {group.files.length > 1 && (
                      <button
                        onClick={() => handleDownloadGroup(group)}
                        className="group relative text-green-600 hover:text-green-800 text-sm flex items-center space-x-1 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-md p-1 transition-colors duration-200"
                        aria-label="Download all files in group as ZIP"
                      >
                        <FaDownload className="text-sm" />
                        <span>Download All</span>
                        <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2">
                          Download as ZIP
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => toggleGroupDetails(groupIndex)}
                      className="group relative text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1 transition-colors duration-200"
                      aria-label={
                        expandedGroupIndex === groupIndex
                          ? "Collapse group details"
                          : "Expand group details"
                      }
                      aria-expanded={expandedGroupIndex === groupIndex}
                    >
                      {expandedGroupIndex === groupIndex ? (
                        <FaChevronUp className="text-sm" />
                      ) : (
                        <FaChevronDown className="text-sm" />
                      )}
                      <span>Details</span>
                      <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2">
                        {expandedGroupIndex === groupIndex ? "Hide" : "Show"} Details
                      </span>
                    </button>
                  </div>
                </div>

                {/* List of files in the group */}
                <div className="p-4 space-y-2">
                  {group.files.map((file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className={`flex items-center justify-between p-3 rounded-md transition-colors duration-200 ${
                        fileIndex % 2 === 0 ? "bg-white" : "bg-gray-100"
                      } hover:bg-blue-50`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-700 font-medium">{file.originalName}</span>
                        {(file.copies > 1 || file.note) && (
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            {file.copies > 1 && <span>({file.copies} copies)</span>}
                            {file.note && <span className="italic">Note: {file.note}</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewFile(file)}
                          className="group relative text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1 transition-colors duration-200"
                          aria-label={`View file ${file.originalName}`}
                        >
                          <FaEye className="text-sm" />
                          <span>View</span>
                          <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2">
                            View File
                          </span>
                        </button>
                        <button
                          onClick={() => handlePrint(file)}
                          className="group relative text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1 transition-colors duration-200"
                          aria-label={`Print file ${file.originalName}`}
                        >
                          <FaPrint className="text-sm" />
                          <span>Print</span>
                          <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2">
                            Print File
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Group customization details */}
                {expandedGroupIndex === groupIndex && (
                  <div className="p-6 border-t border-gray-200 bg-white rounded-b-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Group Customizations
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <FaPalette className="text-blue-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-blue-600">Color Type</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.colorType || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaCopy className="text-green-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-green-600">Copies</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.copies || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaBook className="text-green-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-green-600">Binding</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.wantBinding ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaFile className="text-purple-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-purple-600">Total Pages</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.pageRange === "all"
                              ? group.customizations.totalPages || "N/A"
                              : `${group.customizations.totalPages || "N/A"} (${
                                  group.customizations.pageRange
                                } print)`}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaPaperclip className="text-red-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-red-600">Paper Type</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.paperType || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaPaperPlane className="text-orange-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-orange-600">Paper Size</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.paperSize || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaCompass className="text-teal-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-teal-600">Orientation</span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.orientation || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaLayerGroup className="text-pink-600" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs font-semibold text-pink-600">
                            Pages per Sheet
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {group.customizations.pagesPerSheet || "N/A"}
                          </span>
                        </div>
                      </div>
                      {group.customizations.note && (
                        <div className="col-span-1 sm:col-span-2 flex items-start space-x-2">
                          <FaStickyNote className="text-indigo-600 mt-1" />
                          <div className="flex flex-col space-y-1">
                            <span className="text-xs font-semibold text-indigo-600">Note</span>
                            <span className="text-sm font-medium text-gray-900">
                              {group.customizations.note}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewFilesModal;
